import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Button, Spinner } from '@w7/ui'
import {
  clearAllAuth,
  isAccessTokenExpired,
  logout,
  refreshAccessToken,
  rememberNext,
  resolveAuthPhase,
  resolveConsoleHierarchyGate,
  type AuthPhase,
} from '@/auth/oidc'
import { startSessionWatch } from '@/auth/sessionWatch'
import { flashToast, toastCopy } from '@/ui/toaster'
import { CreditStreamProvider } from '@/app/CreditStreamBridge'

/**
 * Gates the authenticated shell. Public routes (/login, /callback, /no-access)
 * render outside this component.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  // Resolve synchronously so pathname changes don’t flash “Checking session…”
  const [phase, setPhase] = useState<AuthPhase>(() => resolveAuthPhase())
  const [hierarchyGate, setHierarchyGate] = useState<'idle' | 'checking' | 'denied' | 'ok'>('idle')

  useEffect(() => {
    setPhase(resolveAuthPhase())
  }, [location.pathname, location.search])

  useEffect(() => {
    if (phase.phase !== 'expired') return
    let cancelled = false
    ;(async () => {
      const ok = await refreshAccessToken()
      if (cancelled) return
      if (ok && !isAccessTokenExpired()) {
        setPhase(resolveAuthPhase())
        return
      }
      clearAllAuth()
      flashToast(toastCopy.loginExpired)
      setPhase({ phase: 'anon' })
    })()
    return () => {
      cancelled = true
    }
  }, [phase.phase])

  // Company Master (level 1): JWT may still carry `agent` from older provision.
  // When hierarchyLevel claim is absent, verify against hierarchy API.
  useEffect(() => {
    if (phase.phase !== 'ready') {
      setHierarchyGate('idle')
      return
    }
    const who = phase.who
    if (who.hierarchyLevel === '1') {
      setHierarchyGate('denied')
      return
    }
    if (who.hierarchyLevel || !who.agentId) {
      setHierarchyGate('ok')
      return
    }
    let cancelled = false
    setHierarchyGate('checking')
    void resolveConsoleHierarchyGate(who.agentId).then((gate) => {
      if (cancelled) return
      setHierarchyGate(gate === 'denied' ? 'denied' : 'ok')
    })
    return () => {
      cancelled = true
    }
  }, [phase])

  // 1h absolute + 30m idle (also refreshes access tokens while active).
  useEffect(() => {
    if (phase.phase !== 'ready' || hierarchyGate !== 'ok') return
    return startSessionWatch()
  }, [phase.phase, hierarchyGate])

  if (phase.phase === 'loading') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-loading">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'anon') {
    rememberNext(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }

  if (phase.phase === 'expired') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-refreshing">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Refreshing session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'no-access' || hierarchyGate === 'denied') {
    const isCm =
      hierarchyGate === 'denied' ||
      (phase.phase === 'ready' && phase.who.hierarchyLevel === '1') ||
      (phase.phase === 'no-access' && phase.who?.hierarchyLevel === '1')
    return <Navigate to={isCm ? '/no-access?reason=company-master' : '/no-access'} replace />
  }

  if (phase.phase === 'ready' && (hierarchyGate === 'checking' || hierarchyGate === 'idle')) {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-hierarchy-check">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking access…
        </p>
      </div>
    )
  }

  if (phase.phase === 'ready' && hierarchyGate === 'ok') {
    return <CreditStreamProvider>{children}</CreditStreamProvider>
  }

  return (
    <div className="w7-auth w7-auth-center">
      <p>Unexpected auth state.</p>
      <Button onClick={() => logout()}>Sign out</Button>
    </div>
  )
}
